import React, { useState } from "react";
import {
  UserCheck,
  UserX,
  UserMinus,
  Check,
  AlertCircle,
  ArrowRight,
  Settings,
  Clock,
} from "lucide-react";
import { updateMemberAuthStatus } from "../../../services/memberServices";

const StatusUpdateComponent = ({ memberId, initialStatus = "active" }) => {
  console.log(initialStatus, "initialStatus");

  // Status configuration with hex colors instead of Tailwind classes
  const statusConfig = {
    active: {
      label: "Active",
      icon: UserCheck,
      color: "#22c55e",
      bgColor: "#f0fdf4", // green-50 equivalent
      borderColor: "#bbf7d0", // green-200 equivalent
      textColor: "#166534", // green-800 equivalent
      description: "User account is active and accessible",
    },
    inactive: {
      label: "Inactive",
      icon: UserMinus,
      color: "#f97316",
      bgColor: "#fff7ed", // orange-50 equivalent
      borderColor: "#fed7aa", // orange-200 equivalent
      textColor: "#9a3412", // orange-800 equivalent
      description: "User account is temporarily inactive",
    },
  };

  const [currentStatus, setCurrentStatus] = useState(initialStatus);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Handle status change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    if (message) setMessage("");
    if (error) setError("");
  };

  // Handle form submission with actual API call
  const handleSubmit = async () => {
    if (currentStatus === selectedStatus) {
      setError("Please select a different status to update");
      return;
    }

    if (!memberId) {
      setError("Member ID is required to update status");
      return;
    }

    setLoading(true);
    setMessage("");
    setError("");

    try {
      // Call your actual API
      await updateMemberAuthStatus(memberId, selectedStatus);

      // Update local state on success
      setCurrentStatus(selectedStatus);
      setMessage(
        `Status successfully updated to ${statusConfig[selectedStatus].label}`
      );
    } catch (err) {
      setError(err.message || "Failed to update status");
      // Reset selected status on error
      setSelectedStatus(currentStatus);
    } finally {
      setLoading(false);
    }
  };

  const StatusCard = ({ status, config, isSelected, isCurrent }) => {
    const Icon = config.icon;

    return (
      <div
        onClick={() => !loading && handleStatusChange(status)}
        className={`
          relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md
          ${
            isSelected
              ? "border-[#4F22D8] bg-[#4F22D8]/5 shadow-sm"
              : "border-gray-200 hover:border-gray-300"
          }
          ${loading ? "cursor-not-allowed opacity-50" : ""}
        `}
      >
        {isCurrent && (
          <div className="absolute -top-2 -right-2 bg-[#4F22D8] text-white text-xs font-semibold px-2 py-1 rounded-full">
            Current
          </div>
        )}

        <div className="flex items-center gap-3 mb-2">
          <div
            className="p-2 w-10 h-10 rounded-full"
            style={{ backgroundColor: `${config.color}` }}
          >
            <Icon className="w-5 h-5" style={{ color: "white" }} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{config.label}</h3>
            <p className="text-sm text-gray-600">{config.description}</p>
          </div>
        </div>

        {isSelected && (
          <div className="absolute top-4 right-4">
            <div className="w-4 h-4 bg-[#4F22D8] rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Defensive check to ensure statusConfig exists
  const currentConfig = statusConfig[currentStatus];
  const selectedConfig = statusConfig[selectedStatus];

  if (!currentConfig || !selectedConfig) {
    return <div className="p-4 text-red">Invalid status configuration</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#4F22D8] to-[#6366f1] rounded-full mb-4 shadow-lg">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Login Access
          </h2>
          <p className="text-gray-600">
            Manage user account status and permissions
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {message && (
            <div
              className="mb-6 p-4 rounded-lg flex items-center gap-3"
              style={{
                backgroundColor: "#f0fdf4",
                borderColor: "#bbf7d0",
                border: "1px solid",
              }}
            >
              <Check
                className="w-5 h-5 flex-shrink-0"
                style={{ color: "#16a34a" }}
              />
              <span className="font-medium" style={{ color: "#166534" }}>
                {message}
              </span>
            </div>
          )}

          {error && (
            <div
              className="mb-6 p-4 rounded-lg flex items-center gap-3"
              style={{
                backgroundColor: "#fef2f2",
                borderColor: "#fecaca",
                border: "1px solid",
              }}
            >
              <AlertCircle
                className="w-5 h-5 flex-shrink-0"
                style={{ color: "#dc2626" }}
              />
              <span className="font-medium" style={{ color: "#991b1b" }}>
                {error}
              </span>
            </div>
          )}

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  Current Status
                </h4>
                <div className="flex items-center gap-2">
                  <div
                    className="px-3 py-1 rounded-full text-sm font-medium border"
                    style={{
                      backgroundColor: currentConfig.bgColor,
                      color: currentConfig.textColor,
                      borderColor: currentConfig.borderColor,
                    }}
                  >
                    {currentConfig.label}
                  </div>
                  <span className="text-sm text-gray-600">
                    • {currentConfig.description}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Select New Status
            </h3>

            {Object.entries(statusConfig).map(([status, config]) => (
              <StatusCard
                key={status}
                status={status}
                config={config}
                isSelected={selectedStatus === status}
                isCurrent={currentStatus === status}
              />
            ))}
          </div>

          {selectedStatus !== currentStatus && (
            <div
              className="mb-6 p-4 rounded-lg border"
              style={{ backgroundColor: "#eff6ff", borderColor: "#bfdbfe" }}
            >
              <h4 className="font-semibold mb-2" style={{ color: "#1e3a8a" }}>
                Status Change Summary
              </h4>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span style={{ color: "#1e40af" }}>From:</span>
                  <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{
                      backgroundColor: currentConfig.bgColor,
                      color: currentConfig.textColor,
                    }}
                  >
                    {currentConfig.label}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: "#2563eb" }} />
                <div className="flex items-center gap-2">
                  <span style={{ color: "#1e40af" }}>To:</span>
                  <span
                    className="px-2 py-1 rounded text-xs font-medium"
                    style={{
                      backgroundColor: selectedConfig.bgColor,
                      color: selectedConfig.textColor,
                    }}
                  >
                    {selectedConfig.label}
                  </span>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || currentStatus === selectedStatus}
            className="w-full bg-gradient-to-r from-[#4F22D8] to-[#6366f1] text-white font-semibold py-3 px-6 rounded-lg hover:from-[#4F22D8]/90 hover:to-[#6366f1]/90 focus:ring-4 focus:ring-[#4F22D8]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Updating Status...
              </>
            ) : (
              <>
                Update Status
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <div
            className="mt-6 p-4 rounded-lg border"
            style={{ backgroundColor: "#fffbeb", borderColor: "#fde68a" }}
          >
            <h4
              className="text-sm font-semibold mb-2"
              style={{ color: "#92400e" }}
            >
              ⚠️ Important Notice:
            </h4>
            <ul className="text-xs space-y-1" style={{ color: "#92400e" }}>
              <li>• Status changes take effect immediately</li>
              <li>• Suspended users will lose access to all services</li>
              <li>• Inactive users can reactivate their accounts</li>
              <li>• All status changes are logged for audit purposes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateComponent;
